import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Heart, Mail, Lock, ArrowLeft, Eye, EyeOff } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAppMode } from "@/context/AppModeContext";
import { fireHeartsConfetti, fireDiamondsConfetti } from "@/lib/confetti";
import { useLocale } from "@/i18n/LocaleContext";

const DLUXE_LOGIN_IMG = "https://static.prod-images.emergentagent.com/jobs/e1250066-ed24-4faf-8451-7a13993519fa/images/40fdaa6d73e339cf806923a0e140d3c6d1ed26574f7493743bca4d83f9c83cb7.jpeg";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { isDluxe, appName } = useAppMode();
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      (isDluxe ? fireDiamondsConfetti : fireHeartsConfetti)();
      toast.success("Welcome back!");
      navigate("/discover");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/discover";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-12">
        <div className="max-w-md w-full mx-auto">
          {/* Back button */}
          <div className="flex items-center justify-between mb-2">
            <Button
              variant="ghost"
              className="text-muted-foreground hover:text-white -ml-2"
              onClick={() => navigate("/")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("login.backHome")}
            </Button>
            <LanguageSwitcher />
          </div>
          <p className="text-xs text-muted-foreground mb-6" data-testid="login-lang-note">🌍 {t("landing.langHighlight")}</p>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            {isDluxe ? (
              <img src="/logo-dluxe.png" alt="DLuxe-meet.com" className="h-9 w-auto" data-testid="login-dluxe-logo" />
            ) : (
              <>
                <Heart className="w-8 h-8 text-primary fill-primary" />
                <span className="text-2xl font-bold font-['Outfit'] tracking-tight">{appName}</span>
              </>
            )}
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight mb-2">
              {t("login.welcomeBack")}
            </h1>
            <p className="text-muted-foreground">
              {t("login.subtitle")}
            </p>
          </div>

          {/* Google Login */}
          <Button
            variant="outline"
            className="w-full mb-6 py-6 border-white/10 hover:bg-white/5"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t("login.google")}
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-background text-muted-foreground">{t("login.orEmail")}</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">{t("login.email")}</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t("login.emailPh")}
                  className="pl-11 h-12 bg-white/5 border-white/10 rounded-xl"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">{t("login.password")}</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  placeholder={t("login.passwordPh")}
                  className="pl-11 pr-11 h-12 bg-white/5 border-white/10 rounded-xl"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                  tabIndex={-1}
                  data-testid="toggle-password-visibility"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-primary/90 rounded-xl font-semibold"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                t("auth.signIn")
              )}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary">
              {t("login.forgot")}
            </Link>
          </div>

          <p className="mt-8 text-center text-muted-foreground">
            {t("login.noAccount")}{" "}
            <Link to="/register" className="text-primary hover:underline font-medium" data-testid="register-link">
              {t("login.createOne")}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0">
          <img
            src={isDluxe ? DLUXE_LOGIN_IMG : "https://images.pexels.com/photos/950760/pexels-photo-950760.jpeg?auto=compress&cs=tinysrgb&w=1200"}
            alt={appName}
            className={`w-full h-full object-cover ${isDluxe ? "object-top" : ""}`}
            data-testid="login-side-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        </div>
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-2xl font-['Outfit'] leading-relaxed">
            {isDluxe
              ? '"On DLuxe I make the first move — and the men are absolutely worth it."'
              : '"I found someone who truly values quality conversations. LuxeMeet changed everything."'}
          </blockquote>
          <p className="mt-4 text-muted-foreground">{isDluxe ? "— Alexis, 31" : "— Sarah, 28"}</p>
        </div>
      </div>
    </div>
  );
}
